import {
  Cesium3DTileset,
  Model,
  JulianDate,
  Entity,
  Primitive,
  GroundPrimitive,
  defined,
} from "@cesium/engine";
import EditPrimitive from "./EditPrimitive";
import Edit3DTileset from "./Edit3DTileset";
import EditEntity from "./EditEntity";
import EditModel from "./EditModel";
import PointEntity from "../PointEntity";
import LinestringEntity from "../LinestringEntity";
import PolygonEntity from "../PolygonEntity";
import EditDitapGeometry from "./EditDitapGeometry";

// /**
//  * Remove less than 8 decimal places of input object
//  * @param {Cartesian3 | HeadingPitchRoll | Quaternion | Matrix3 | Matrix4} object
//  * @param {number} [range=7] range
//  * @returns {Cartesian3 | HeadingPitchRoll | Quaternion | Matrix3 | Matrix4} Object with decimal point removed
//  */
// removeByEpsilon(object, range = 7) {
//   //const multiplyValue = 10000000;
//   //const divideValue = 10000000;

//   let newObject;
//   if (object instanceof Cartesian3) {
//     newObject = new Cartesian3();
//   } else if (object instanceof HeadingPitchRoll) {
//     newObject = new HeadingPitchRoll();
//   } else if (object instanceof Quaternion) {
//     newObject = new Quaternion();
//   } else if (object instanceof Matrix3) {
//     newObject = new Matrix3();
//   } else if (object instanceof Matrix4) {
//     newObject = new Matrix4();
//   } else {
//     return;
//   }

//   Object.keys(object).forEach(function (key) {
//     //newObject[key] = Math.round(object[key] * multiplyValue) / divideValue;
//     newObject[key] = Number(object[key].toFixed(range));
//   });

//   return newObject;
// }

/*
 * Cesium Object Editing Class.<br>
 * @alias EditObject
 * @private
 */
class EditObject {
  /**
   * BoundingSphere 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @returns {BoundingSphere | undefined}
   */
  static getBoundingSphere(object) {
    let boundingSphere;
    if (object instanceof Model) {
      boundingSphere = EditModel.getBoundingSphere(object);
    } else if (object instanceof Cesium3DTileset) {
      boundingSphere = Edit3DTileset.getBoundingSphere(object);
    } else if (object instanceof Primitive) {
      boundingSphere = EditPrimitive.getBoundingSphere(object);
    } else if (object instanceof Entity) {
      boundingSphere = EditEntity.getBoundingSphere(object);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      boundingSphere = EditDitapGeometry.getBoundingSphere(object);
    }

    return boundingSphere;
  }

  /**
   * Transform 정보 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @param {boolean} [enu=true] East-North-Up 기준 추출.<br> East-North-Up criteria extraction.
   * @returns {{
   *  position: Cartesian3,
   *  originalPosition: Cartesian3,
   *  translation: Cartesian3,
   *  quaternion: Quaternion,
   *  HeadingPitchRoll: HeadingPitchRoll,
   *  scale: Cartesian3
   * }}
   */
  static decomposite(object, enu = true) {
    let decomposition;
    if (object instanceof Model) {
      decomposition = EditModel.decomposition(object, enu);
    } else if (object instanceof Cesium3DTileset) {
      decomposition = Edit3DTileset.decomposition(object, enu);
    } else if (object instanceof Primitive) {
      decomposition = EditPrimitive.decomposition(object, enu);
    } else if (object instanceof Entity) {
      decomposition = EditEntity.decomposition(object, enu);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      decomposition = EditDitapGeometry.decomposition(object, enu);
    }

    return decomposition;
  }

  /**
   * 좌표 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @returns {Cartesian3 | undefined}
   */
  static getPosition(object) {
    let position;
    if (object instanceof Model) {
      position = EditModel.getPosition(object);
    } else if (object instanceof Cesium3DTileset) {
      position = Edit3DTileset.getPosition(object);
    } else if (object instanceof Entity) {
      position = EditEntity.getPosition(object);
    } else if (object instanceof Primitive) {
      position = EditPrimitive.getPosition(object);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      position = EditDitapGeometry.getPosition(object);
    }

    return position;
  }

  /**
   * 좌표 설정
   * @param {Model | Cesium3DTileset | Primitive | Entity} object
   * @param {Cartesian3 | Property} position
   * @returns {Cartesian3 | Property}
   */
  static setPosition(object, position) {
    // Entity가 아닌 경우 position은 Cartesian3만 지원
    let _position = position;
    if (
      !(
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      ) &&
      defined(position.getValue)
    ) {
      _position = position.getValue(new JulianDate());
    }

    // 오브젝트 좌표 업데이트
    let result;
    if (object instanceof Model) {
      result = EditModel.setPosition(object, _position);
    } else if (object instanceof Cesium3DTileset) {
      result = Edit3DTileset.setPosition(object, _position);
    } else if (object instanceof Entity) {
      result = EditEntity.setPosition(object, _position);
    } else if (object instanceof Primitive) {
      result = EditPrimitive.setPosition(object, _position);
    } else if (object instanceof GroundPrimitive) {
      result = EditPrimitive.setPosition(object, _position);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      result = EditDitapGeometry.setPosition(object, _position);
    }

    return result;
  }

  /**
   * Quaternion 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @param {boolean} [enu=true] EastNorthUp 기준 Quaternion 추출
   * @returns {Quaternion}
   */
  static getQuaternion(object, enu = true) {
    let quaternion;
    if (object instanceof Entity) {
      quaternion = EditEntity.getQuaternion(object, enu);
    } else if (object instanceof Cesium3DTileset) {
      quaternion = Edit3DTileset.getQuaternion(object, enu);
    } else if (object instanceof Model) {
      quaternion = EditModel.getQuaternion(object, enu);
    } else if (object instanceof Primitive) {
      quaternion = EditPrimitive.getQuaternion(object, enu);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      quaternion = EditDitapGeometry.getQuaternion(object, enu);
    }

    return quaternion;
  }

  /**
   * HeadingPitchRoll 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @param {boolean} [enu=true] EastNorthUp 기준 Quaternion 추출
   * @returns {HeadingPitchRoll}
   */
  static getHeadingPitchRoll(object, enu) {
    let quaternion;
    if (object instanceof Entity) {
      quaternion = EditEntity.getHeadingPitchRoll(object, enu);
    } else if (object instanceof Cesium3DTileset) {
      quaternion = Edit3DTileset.getHeadingPitchRoll(object, enu);
    } else if (object instanceof Model) {
      quaternion = EditModel.getHeadingPitchRoll(object, enu);
    } else if (object instanceof Primitive) {
      quaternion = EditPrimitive.getHeadingPitchRoll(object, enu);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      quaternion = EditDitapGeometry.getHeadingPitchRoll(object, enu);
    }

    return quaternion;
  }

  /**
   * 회전 설정
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @param {Quaternion | HeadingPitchRoll | Property} rotation
   * @returns {Quaternion | HeadingPitchRoll | Property}
   */
  static setRotation(object, rotation) {
    // Entity가 아닌 경우 position은 HeadingPitchRoll, Quaternion만 지원
    let _rotation = rotation;
    if (
      !(
        object instanceof Entity ||
        object instanceof PointEntity ||
        object instanceof LinestringEntity ||
        object instanceof PolygonEntity
      ) &&
      defined(rotation.getValue)
    ) {
      _rotation = rotation.getValue(new JulianDate());
    }

    // 오브젝트 업데이트
    let result;
    if (object instanceof Model) {
      result = EditModel.setRotation(object, _rotation);
    } else if (object instanceof Cesium3DTileset) {
      result = Edit3DTileset.setRotation(object, _rotation);
    } else if (object instanceof Entity) {
      result = EditEntity.setRotation(object, _rotation);
    } else if (object instanceof Primitive) {
      result = EditPrimitive.setRotation(object, _rotation);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      result = EditDitapGeometry.setRotation(object, _rotation);
    }

    return result;
  }

  /**
   * Scale 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @returns {Cartesian3}
   */
  static getScale(object) {
    let scale;
    if (object instanceof Cesium3DTileset) {
      scale = Edit3DTileset.getScale(object);
    } else if (object instanceof Model) {
      scale = EditModel.getScale(object);
    } else if (object instanceof Primitive) {
      scale = EditPrimitive.getScale(object);
    } else if (object instanceof Entity) {
      scale = EditEntity.getScale(object);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      scale = EditDitapGeometry.getScale(object);
    }

    return scale;
  }

  /**
   * Scale 설정
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @param {Cartesian3 | Property} scale
   * @returns {Cartesian3 | Property}
   */
  static setScale(object, scale) {
    let result;
    if (object instanceof Cesium3DTileset) {
      result = Edit3DTileset.setScale(object, scale);
    } else if (object instanceof Model) {
      result = EditModel.setScale(object, scale);
    } else if (object instanceof Primitive) {
      result = EditPrimitive.setScale(object, scale);
    } else if (object instanceof Entity) {
      result = EditEntity.setScale(object, scale);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      result = EditDitapGeometry.setScale(object, scale);
    }

    return result;
  }

  /**
   * Matrix4 추출
   * @param {Model | Cesium3DTileset | Primitive | Entity | PointEntity | LinestringEntity | PolygonEntity} object
   * @returns {Matrix4}
   */
  static getMatrix(object) {
    let matrix;
    if (object instanceof Cesium3DTileset) {
      matrix = Edit3DTileset.getMatrix(object);
    } else if (object instanceof Model) {
      matrix = EditModel.getMatrix(object);
    } else if (object instanceof Primitive) {
      matrix = EditPrimitive.getMatrix(object);
    } else if (object instanceof Entity) {
      matrix = EditEntity.getMatrix(object);
    } else if (
      object instanceof PointEntity ||
      object instanceof LinestringEntity ||
      object instanceof PolygonEntity
    ) {
      matrix = EditDitapGeometry.getMatrix(object);
    }

    return matrix;
  }

  /**
   * Matrix 설정
   * @param {Model | Cesium3DTileset | Primitive} object
   * @param {Matrix4} matrix
   * @returns {Matrix4}
   */
  static setMatrix(object, matrix) {
    if (object instanceof Cesium3DTileset) {
      Edit3DTileset.setMatrix(object);
    } else if (object instanceof Model) {
      EditModel.setMatrix(object);
    } else if (object instanceof Primitive) {
      EditPrimitive.setMatrix(object);
    }

    return matrix;
  }
}

export default EditObject;
